import React, { useState, useEffect, useRef, useMemo } from "react";
import PageHeader from "shared/components/PageHeader/PageHeader";
import JSZip from "jszip";
import {
    Book,
    Plus,
    Search,
    Edit,
    Trash2,
    Eye,
    Upload,
    Save,
    X,
    CheckCircle,
    AlertCircle,
    Download,
    List,
    Archive,
    Layers,
    XCircle,
    Calendar,
    ArrowLeft,
    HelpCircle,
} from "react-feather";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    orderBy,
    where,
    serverTimestamp,
} from "firebase/firestore";
import { db, storage, auth, saveReadModuleQuiz, getReadModuleQuizForAdmin } from "shared/utils/firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import styles from "./readmoduleManagement.module.css";
import MarkdownContentRenderer from "./MarkdownContentRenderer";


function ReadModuleManagement() {
    const [activeSubTab, setActiveSubTab] = useState("modules");

    // In-Page View State: "catalog" (modules list) vs "workspace" (in-page module editor)
    const [activeView, setActiveView] = useState("catalog");
    const [workspaceTab, setWorkspaceTab] = useState("topics"); // "topics" | "details" | "quiz"

    // Modules State
    const [modules, setModules] = useState([]);
    const [filteredModules, setFilteredModules] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showModuleModal, setShowModuleModal] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingModule, setViewingModule] = useState(null);
    const [showCSVUpload, setShowCSVUpload] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState({ message: "", type: "" });

    // Topics State (for managing topics within a module in 25/75 split view)
    const [moduleTopics, setModuleTopics] = useState([]);
    const [editingTopic, setEditingTopic] = useState(null);
    const [showTopicForm, setShowTopicForm] = useState(false);
    const [showTopicCSVUpload, setShowTopicCSVUpload] = useState(false);
    const [topicCsvFile, setTopicCsvFile] = useState(null);
    const [topicUploadStatus, setTopicUploadStatus] = useState({ message: "", type: "", errors: [] });
    const [topicSearchQuery, setTopicSearchQuery] = useState("");
    const [topicRightPaneMode, setTopicRightPaneMode] = useState("edit"); // "edit" | "preview"
    const [topicForm, setTopicForm] = useState({
        title: "",
        content: "",
        order: 1,
    });

    const filteredSidebarTopics = useMemo(() => {
        if (!topicSearchQuery) return moduleTopics;
        return moduleTopics.filter(t =>
            (t.title || "").toLowerCase().includes(topicSearchQuery.toLowerCase())
        );
    }, [moduleTopics, topicSearchQuery]);

    // ZIP Bundle Import State
    const [showZipImport, setShowZipImport] = useState(false);
    const [zipFile, setZipFile] = useState(null);
    const [zipImportStatus, setZipImportStatus] = useState({ message: "", type: "", details: [] });
    const [zipImportProgress, setZipImportProgress] = useState({ phase: "", current: 0, total: 0 });
    const [zipImporting, setZipImporting] = useState(false);

    // Image Upload State for Topics
    const contentTextareaRef = useRef(null);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [imageFile, setImageFile] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageUploadProgress, setImageUploadProgress] = useState(0);
    const [imageUploadError, setImageUploadError] = useState("");
    const [imageUploadSuccess, setImageUploadSuccess] = useState(false);

    // Quiz State
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [loadingQuiz, setLoadingQuiz] = useState(false);
    const [savingQuiz, setSavingQuiz] = useState(false);
    const [quizSaveStatus, setQuizSaveStatus] = useState({ message: '', type: '' });
    const [quizForm, setQuizForm] = useState({
        passingScore: 70,
        questionCount: 20,
        isPublished: false,
        questions: []
    });
    const [quizDocId, setQuizDocId] = useState(null);

    // Quiz Reset Requests State
    const [quizRequests, setQuizRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);

    const categories = [
        "Basic Electronics",
        "VLSI Design",
        "Embedded Systems & IoT",
        "Communications & Networking",
        "Artificial Intelligence",
        "Projects",
        "Programming Languages",
        "General Tech"
    ];

    const [moduleForm, setModuleForm] = useState({
        title: "",
        description: "",
        category: "",
        content: "",
        duration: "",
        difficulty: "Beginner",
        tags: "",
        isPublished: false,
        author: "",
        siliconPoints: 50,
    });

    const handleOpenWorkspace = async (module = null, tab = "topics") => {
        if (module) {
            setViewingModule(module);
            setEditingModule(module);
            setModuleForm({
                title: module.title || "",
                description: module.description || "",
                category: module.category || "",
                content: module.content || "",
                duration: module.duration || "",
                difficulty: module.difficulty || "Beginner",
                tags: Array.isArray(module.tags) ? module.tags.join(", ") : (module.tags || ""),
                isPublished: module.isPublished || false,
                author: module.author || "",
                siliconPoints: module.siliconPoints !== undefined ? Number(module.siliconPoints) : 50,
            });
            await fetchModuleTopics(module.id);
            if (tab === "quiz") {
                await fetchQuizForWorkspace(module.id);
            }
        } else {
            setViewingModule(null);
            setEditingModule(null);
            setModuleTopics([]);
            resetForm();
            tab = "details";
        }
        setWorkspaceTab(tab);
        setActiveView("workspace");
        setShowTopicForm(false);
        setShowTopicCSVUpload(false);
        setShowZipImport(false);
    };

    const handleWorkspaceTabChange = async (tab) => {
        setWorkspaceTab(tab);
        if (tab === "quiz" && viewingModule?.id) {
            await fetchQuizForWorkspace(viewingModule.id);
        }
    };

    const fetchQuizForWorkspace = async (moduleId) => {
        if (!moduleId) return;
        setQuizDocId(null);
        setQuizSaveStatus({ message: '', type: '' });
        setLoadingQuiz(true);

        try {
            const existing = await getReadModuleQuizForAdmin(moduleId);
            if (existing) {
                setQuizDocId(existing.id);
                setQuizForm({
                    passingScore:  existing.passingScore  ?? 70,
                    questionCount: existing.questionCount ?? 20,
                    isPublished:   existing.isPublished   ?? false,
                    questions:     existing.questions     || []
                });
            } else {
                setQuizForm({ passingScore: 70, questionCount: 20, isPublished: false, questions: [] });
            }
        } catch (err) {
            console.error('fetchQuizForWorkspace error:', err);
        } finally {
            setLoadingQuiz(false);
        }
    };

    const handleSaveQuiz = async () => {
        if (!viewingModule?.id) {
            setQuizSaveStatus({ message: 'Please save module details first before saving a quiz.', type: 'error' });
            return;
        }
        if (quizForm.questions.length < 1) {
            setQuizSaveStatus({ message: 'Please add at least 1 question before saving.', type: 'error' });
            return;
        }

        const invalid = quizForm.questions.find(q => !q.questionText?.trim() || !q.correctOption);
        if (invalid) {
            setQuizSaveStatus({ message: 'Each question must have question text and a correct option selected.', type: 'error' });
            return;
        }

        setSavingQuiz(true);
        setQuizSaveStatus({ message: '', type: '' });

        try {
            const payload = {
                courseId:      viewingModule?.category || '',
                moduleId:      viewingModule?.id || '',
                moduleTitle:   viewingModule?.title || '',
                courseTitle:   viewingModule?.category || '',
                passingScore:  quizForm.passingScore,
                questionCount: quizForm.questionCount,
                isPublished:   quizForm.isPublished,
                questions:     quizForm.questions
            };

            const result = await saveReadModuleQuiz(payload, quizDocId);
            if (result.success) {
                setQuizDocId(result.id);
                setQuizSaveStatus({ message: `Quiz saved successfully! (${quizForm.questions.length} questions)`, type: 'success' });
            } else {
                setQuizSaveStatus({ message: `Save failed: ${result.message}`, type: 'error' });
            }
        } catch (err) {
            setQuizSaveStatus({ message: `Error: ${err.message}`, type: 'error' });
        } finally {
            setSavingQuiz(false);
        }
    };

    const downloadQuizCSVTemplate = () => {
        const csvContent = [
            [
                "questiontext",
                "optiona",
                "optionb",
                "optionc",
                "optiond",
                "correctoption",
                "explanation",
                "marks"
            ].join(","),
            [
                '"What is a Read Module?"',
                '"A module containing text-based learning"',
                '"A video playback module"',
                '"An interactive coding block"',
                '"A structural PDB template"',
                '"A"',
                '"Read Modules are text-based study resources in SilconLogix study portal."',
                '"1"'
            ].join(",")
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `read_module_quiz_template_${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Load modules
    useEffect(() => {
        fetchModules();
    }, []);

    const fetchModules = async () => {
        try {
            const q1 = query(collection(db, "readingModules"), orderBy("createdAt", "desc"));
            const q2 = query(collection(db, "textModules"), orderBy("createdAt", "desc"));

            const [snap1, snap2] = await Promise.all([
                getDocs(q1).catch(() => ({ docs: [] })),
                getDocs(q2).catch(() => ({ docs: [] }))
            ]);

            const modulesMap = new Map();
            [...snap1.docs, ...snap2.docs].forEach(docSnap => {
                if (!modulesMap.has(docSnap.id)) {
                    modulesMap.set(docSnap.id, {
                        id: docSnap.id,
                        ...docSnap.data()
                    });
                }
            });

            const modulesData = Array.from(modulesMap.values());
            setModules(modulesData);
            setFilteredModules(modulesData);
        } catch (error) {
            console.error("Error fetching modules:", error);
        }
    };

    // Fetch quiz reset requests
    const fetchQuizRequests = async () => {
        try {
            setLoadingRequests(true);
            const q = query(collection(db, "quizAttemptRequests"), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            
            const textModuleCategories = [
                "Basic Electronics",
                "VLSI Design",
                "Embedded Systems & IoT",
                "Communications & Networking",
                "Artificial Intelligence",
                "Projects",
                "Programming Languages",
                "General Tech"
            ];

            const requestsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).filter(req => {
                // Only show reading module quiz requests here (not course ones)
                const isCourseQuiz = req.moduleId === 'course_quiz' || (req.courseId && !textModuleCategories.includes(req.courseId));
                return !isCourseQuiz;
            });

            setQuizRequests(requestsData);
        } catch (error) {
            console.error("Error fetching quiz attempt requests:", error);
        } finally {
            setLoadingRequests(false);
        }
    };

    useEffect(() => {
        if (activeSubTab === "quizRequests") {
            fetchQuizRequests();
        }
    }, [activeSubTab]);

    const handleApproveRequest = async (requestId, allowedAttemptsValue) => {
        try {
            await updateDoc(doc(db, "quizAttemptRequests", requestId), {
                status: "approved",
                allowedAttempts: allowedAttemptsValue || 4,
                updatedAt: serverTimestamp()
            });
            setUploadStatus({ message: "Request approved! Attempt limit increased successfully.", type: "success" });
            fetchQuizRequests();
            setTimeout(() => setUploadStatus({ message: "", type: "" }), 3000);
        } catch (error) {
            console.error("Error approving request:", error);
            setUploadStatus({ message: "Error approving request", type: "error" });
        }
    };

    const handleRejectRequest = async (requestId) => {
        try {
            await updateDoc(doc(db, "quizAttemptRequests", requestId), {
                status: "rejected",
                updatedAt: serverTimestamp()
            });
            setUploadStatus({ message: "Request rejected successfully.", type: "success" });
            fetchQuizRequests();
            setTimeout(() => setUploadStatus({ message: "", type: "" }), 3000);
        } catch (error) {
            console.error("Error rejecting request:", error);
            setUploadStatus({ message: "Error rejecting request", type: "error" });
        }
    };

    // Filter modules
    useEffect(() => {
        let filtered = modules;

        if (searchQuery) {
            filtered = filtered.filter(m =>
                m.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                m.description?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (categoryFilter !== "all") {
            filtered = filtered.filter(m => m.category === categoryFilter);
        }

        if (statusFilter !== "all") {
            if (statusFilter === "published") {
                filtered = filtered.filter(m => m.isPublished === true);
            } else {
                filtered = filtered.filter(m => !m.isPublished);
            }
        }

        setFilteredModules(filtered);
    }, [searchQuery, categoryFilter, statusFilter, modules]);

    const resetForm = () => {
        setModuleForm({
            title: "",
            description: "",
            category: "",
            content: "",
            duration: "",
            difficulty: "Beginner",
            tags: "",
            isPublished: false,
            author: "",
            siliconPoints: 50,
        });
        setEditingModule(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const tagList = typeof moduleForm.tags === "string" 
                ? moduleForm.tags.split(",").map(t => t.trim()).filter(t => t) 
                : (moduleForm.tags || []);

            const moduleData = {
                ...moduleForm,
                siliconPoints: Number(moduleForm.siliconPoints) || 50,
                tags: tagList,
                updatedAt: serverTimestamp(),
            };

            const targetId = editingModule?.id || viewingModule?.id;
            if (targetId) {
                await updateDoc(doc(db, "readingModules", targetId), moduleData);
                const updatedMod = { id: targetId, ...moduleData };
                setViewingModule(updatedMod);
                setEditingModule(updatedMod);
                setUploadStatus({ message: "Module updated successfully!", type: "success" });
            } else {
                const docRef = await addDoc(collection(db, "readingModules"), {
                    ...moduleData,
                    createdAt: serverTimestamp(),
                });
                const newMod = { id: docRef.id, ...moduleData };
                setViewingModule(newMod);
                setEditingModule(newMod);
                setUploadStatus({ message: "Module created successfully!", type: "success" });
            }

            fetchModules();
            setTimeout(() => setUploadStatus({ message: "", type: "" }), 3000);
        } catch (error) {
            console.error("Error saving module:", error);
            setUploadStatus({ message: "Error saving module", type: "error" });
        }
    };

    const handleDelete = async (moduleId) => {
        if (!window.confirm("Are you sure you want to delete this module? This will also delete all internal topics belonging to this module.")) return;

        try {
            // 1. Delete all internal topics from readingTopics collection
            const qReading = query(
                collection(db, "readingTopics"),
                where("moduleId", "==", moduleId)
            );
            const snapReading = await getDocs(qReading);
            const deletePromisesReading = snapReading.docs.map(docSnap => deleteDoc(doc(db, "readingTopics", docSnap.id)));
            await Promise.all(deletePromisesReading);

            // 2. Delete the module document itself from readingModules
            await deleteDoc(doc(db, "readingModules", moduleId));

            setUploadStatus({ message: "Module and all internal topics deleted successfully!", type: "success" });
            fetchModules();
            setTimeout(() => setUploadStatus({ message: "", type: "" }), 3000);
        } catch (error) {
            console.error("Error deleting module:", error);
            setUploadStatus({ message: "Error deleting module", type: "error" });
        }
    };

    const handleCSVUpload = async () => {
        if (!csvFile) {
            setUploadStatus({ message: "Please select a CSV file", type: "error" });
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target.result;
                const rows = text.split("\n").filter(row => row.trim());

                if (rows.length < 2) {
                    setUploadStatus({ message: "CSV file is empty or has no data rows", type: "error" });
                    return;
                }

                const headers = rows[0].split(",").map(h => h.trim());
                let successCount = 0;
                let errorCount = 0;

                for (let i = 1; i < rows.length; i++) {
                    try {
                        const values = rows[i].split(",").map(v => v.trim());
                        const moduleData = {};

                        headers.forEach((header, index) => {
                            if (header === "tags") {
                                moduleData[header] = values[index] ? values[index].split(";").map(t => t.trim()).filter(t => t) : [];
                            } else if (header === "isPublished") {
                                moduleData[header] = values[index]?.toLowerCase() === "true";
                            } else if (header === "duration") {
                                moduleData[header] = values[index] || "";
                            } else {
                                moduleData[header] = values[index] || "";
                            }
                        });

                        // Add timestamps for proper sync
                        moduleData.createdAt = serverTimestamp();
                        moduleData.updatedAt = serverTimestamp();

                        await addDoc(collection(db, "readingModules"), moduleData);
                        successCount++;
                    } catch (err) {
                        console.error(`Error uploading row ${i}:`, err);
                        errorCount++;
                    }
                }

                if (errorCount > 0) {
                    setUploadStatus({
                        message: `Uploaded ${successCount} modules with ${errorCount} errors`,
                        type: "warning"
                    });
                } else {
                    setUploadStatus({
                        message: `Successfully uploaded ${successCount} modules!`,
                        type: "success"
                    });
                }

                setShowCSVUpload(false);
                setCsvFile(null);
                fetchModules();
                setTimeout(() => setUploadStatus({ message: "", type: "" }), 3000);
            };
            reader.readAsText(csvFile);
        } catch (error) {
            console.error("Error uploading CSV:", error);
            setUploadStatus({ message: "Error uploading CSV file", type: "error" });
        }
    };

    const downloadCSVTemplate = () => {
        const template = "title,description,category,content,duration,difficulty,tags,isPublished,author\nSample Module,This is a sample module,Basic Electronics,Module content here,30,Beginner,electronics;basics,true,Admin";
        const blob = new Blob([template], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "module_template.csv";
        a.click();
    };

    const exportModules = () => {
        const listToExport = filteredModules.length > 0 ? filteredModules : modules;
        const csv = [
            ["Title", "Category", "Difficulty", "Duration", "Author", "SiliconPoints", "Status", "Tags", "Description"].join(","),
            ...listToExport.map(m => [
                `"${(m.title || "").replace(/"/g, '""')}"`,
                `"${(m.category || "").replace(/"/g, '""')}"`,
                `"${(m.difficulty || "").replace(/"/g, '""')}"`,
                `"${(m.duration || "").replace(/"/g, '""')}"`,
                `"${(m.author || "").replace(/"/g, '""')}"`,
                `"${m.siliconPoints || 50}"`,
                `"${m.isPublished ? "Published" : "Draft"}"`,
                `"${Array.isArray(m.tags) ? m.tags.join(";") : (m.tags || "")}"`,
                `"${(m.description || "").replace(/"/g, '""')}"`
            ].join(","))
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "reading_modules.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // ==================== TOPIC MANAGEMENT FUNCTIONS ====================

    const fetchModuleTopics = async (moduleId) => {
        try {
            const topicQueries = [
                query(collection(db, "readingTopics"), where("moduleId", "==", moduleId)),
                query(collection(db, "readingTopics"), where("module_id", "==", moduleId)),
                query(collection(db, "readingTopics"), where("readModuleId", "==", moduleId)),
                query(collection(db, "readingTopics"), where("textModuleId", "==", moduleId)),
                query(collection(db, "readingTopics"), where("courseId", "==", moduleId)),

                query(collection(db, "textTopics"), where("moduleId", "==", moduleId)),
                query(collection(db, "textTopics"), where("module_id", "==", moduleId)),
                query(collection(db, "textTopics"), where("readModuleId", "==", moduleId)),
                query(collection(db, "textTopics"), where("textModuleId", "==", moduleId)),

                query(collection(db, "topics"), where("moduleId", "==", moduleId)),
                query(collection(db, "topics"), where("module_id", "==", moduleId)),
                query(collection(db, "topics"), where("readModuleId", "==", moduleId)),

                query(collection(db, "moduleTopics"), where("moduleId", "==", moduleId)),
                query(collection(db, "moduleTopics"), where("module_id", "==", moduleId)),

                query(collection(db, "readingModules", moduleId, "topics")),
                query(collection(db, "readingModules", moduleId, "readingTopics")),
                query(collection(db, "textModules", moduleId, "topics")),
                query(collection(db, "textModules", moduleId, "textTopics")),
            ];

            const snapshots = await Promise.all(topicQueries.map(q => getDocs(q).catch(() => ({ docs: [] }))));

            const topicsMap = new Map();
            snapshots.forEach(s => {
                if (s.docs) {
                    s.docs.forEach(docSnap => {
                        if (!topicsMap.has(docSnap.id)) {
                            const d = docSnap.data();
                            const parsedOrder = typeof d.order === "number" ? d.order : (parseInt(d.order, 10) || (typeof d.position === "number" ? d.position : (parseInt(d.position, 10) || 0)));
                            topicsMap.set(docSnap.id, {
                                id: docSnap.id,
                                ...d,
                                title: d.title || d.name || d.topicTitle || "Untitled Topic",
                                content: d.content || d.description || d.body || d.markdown || d.text || "",
                                order: parsedOrder
                            });
                        }
                    });
                }
            });

            const topicsData = Array.from(topicsMap.values());
            topicsData.sort((a, b) => (a.order || 0) - (b.order || 0));
            setModuleTopics(topicsData);
            if (topicsData.length > 0) {
                setEditingTopic(topicsData[0]);
                setTopicForm({
                    title: topicsData[0].title || "",
                    content: topicsData[0].content || "",
                    order: topicsData[0].order || 1,
                });
                const existingImages = scanExistingImages(topicsData[0].content);
                setUploadedImages(existingImages);
            } else {
                setEditingTopic(null);
                setTopicForm({ title: "", content: "", order: 1 });
                setUploadedImages([]);
            }
        } catch (error) {
            console.error("Error fetching topics:", error);
            setModuleTopics([]);
        }
    };

    const handleViewModuleWithTopics = async (module) => {
        await handleOpenWorkspace(module, "topics");
    };

    const handleTopicInputChange = (e) => {
        const { name, value } = e.target;
        setTopicForm({ ...topicForm, [name]: value });
    };

    const resetTopicForm = () => {
        setTopicForm({
            title: "",
            content: "",
            order: moduleTopics.length + 1,
        });
        setEditingTopic(null);
        setImageFile(null);
        setImageUploadProgress(0);
        setImageUploadError("");
        setImageUploadSuccess(false);
    };

    const handleAddTopic = () => {
        resetTopicForm();
        setUploadedImages([]);
        setImageFile(null);
        setImageUploadError("");
        setImageUploadSuccess(false);
        setShowTopicForm(true);
    };

    const scanExistingImages = (content) => {
        if (!content) return [];
        const regex = /<img[^>]+src="([^">]+)"/g;
        const urls = [];
        let match;
        while ((match = regex.exec(content)) !== null) {
            urls.push(match[1]);
        }
        return urls;
    };

    const handleEditTopic = (topic) => {
        setEditingTopic(topic);
        setTopicForm({
            title: topic.title || "",
            content: topic.content || "",
            order: topic.order || 1,
        });
        const existingImages = scanExistingImages(topic.content);
        setUploadedImages(existingImages);
        setImageFile(null);
        setImageUploadError("");
        setImageUploadSuccess(false);
        setShowTopicForm(true);
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImageUploadError("");
        }
    };

    const uploadTopicImageToFirebase = () => {
        if (!imageFile) {
            setImageUploadError("Please select an image file first.");
            return;
        }

        // ✅ Auth check - Storage rules require authenticated admin
        if (!auth.currentUser) {
            setImageUploadError("You must be logged in to upload images.");
            return;
        }

        setUploadingImage(true);
        setImageUploadProgress(0);
        setImageUploadError("");

        try {
            console.log("Starting topic image upload for file:", imageFile.name, "| Size:", (imageFile.size / 1024).toFixed(1), "KB");
            console.log("Current user UID:", auth.currentUser.uid);
            
            // Validate file size (5MB)
            const fileSizeMB = imageFile.size / 1024 / 1024;
            if (fileSizeMB > 5) {
                setUploadingImage(false);
                setImageUploadError("Image must be smaller than 5MB.");
                return;
            }

            // Create storage path
            const timestamp = Date.now();
            const categoryFolder = (viewingModule?.category || "General")
                .replace(/[^a-zA-Z0-9]/g, "_")
                .replace(/_+/g, "_")
                .toLowerCase();

            const safeFileName = imageFile.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
            const uniqueFileName = `textmodule_topics/${categoryFolder}/${timestamp}_${safeFileName}`;
            console.log("Upload path:", uniqueFileName);
            const storageRef = ref(storage, uniqueFileName);

            // ✅ Include metadata so Firebase can verify contentType in rules
            const uploadMetadata = {
                contentType: imageFile.type,
            };

            const task = uploadBytesResumable(storageRef, imageFile, uploadMetadata);

            task.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`Upload progress: ${Math.round(progress)}% (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`);
                    setImageUploadProgress(Math.round(progress));
                },
                (error) => {
                    // Detailed error reporting for each Firebase Storage error code
                    console.error("Firebase Storage upload error:", error.code, error.message);
                    let userMsg = `Upload failed: ${error.message}`;
                    if (error.code === 'storage/unauthorized') {
                        userMsg = "Permission denied. Make sure storage rules allow admin uploads to textmodule_topics/. Deploy the updated firestore.rules.";
                    } else if (error.code === 'storage/canceled') {
                        userMsg = "Upload was cancelled.";
                    } else if (error.code === 'storage/unknown') {
                        userMsg = "Unknown error. Check browser console and Firebase Storage rules.";
                    } else if (error.code === 'storage/quota-exceeded') {
                        userMsg = "Firebase Storage quota exceeded.";
                    } else if (error.code === 'storage/unauthenticated') {
                        userMsg = "Session expired. Please log out and log back in.";
                    }
                    setUploadingImage(false);
                    setImageUploadError(userMsg);
                },
                async () => {
                    try {
                        console.log("Upload completed, fetching download URL...");
                        const downloadUrl = await getDownloadURL(storageRef);
                        console.log("Successfully fetched download URL:", downloadUrl);

                        // Step 1: Show 100% progress FIRST (keep uploadingImage true briefly)
                        setImageUploadProgress(100);

                        // Step 2: After a short delay show success badge
                        setTimeout(() => {
                            setUploadingImage(false);
                            setImageUploadSuccess(true);
                            setImageFile(null);
                            setUploadedImages((prev) => [...prev, downloadUrl]);
                            // Auto-clear success badge after 4 seconds
                            setTimeout(() => setImageUploadSuccess(false), 4000);
                        }, 600);

                        // Automatically insert image into textarea at cursor
                        insertImageAtCursor(downloadUrl);
                    } catch (urlError) {
                        console.error("Error getting download URL:", urlError);
                        setUploadingImage(false);
                        setImageUploadError(`Failed to fetch image URL: ${urlError.message}`);
                    }
                }
            );
        } catch (err) {
            console.error("Synchronous error during image upload setup:", err);
            setUploadingImage(false);
            setImageUploadError(`Upload setup failed: ${err.message}`);
        }
    };

    const insertImageAtCursor = (url) => {
        const textarea = contentTextareaRef.current;
        const imgTag = `![Topic Image](${url})`;
        
        if (!textarea) {
            // Fallback: append if ref isn't available
            setTopicForm(prev => ({
                ...prev,
                content: (prev.content || "") + `\n${imgTag}`
            }));
            return;
        }

        const startPos = textarea.selectionStart;
        const endPos = textarea.selectionEnd;
        const text = topicForm.content || "";
        const newText = text.substring(0, startPos) + imgTag + text.substring(endPos);

        setTopicForm(prev => ({
            ...prev,
            content: newText
        }));

        // Refocus textarea and put cursor after the inserted image tag
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = startPos + imgTag.length;
        }, 50);
    };

    const handleTopicSubmit = async (e) => {
        e.preventDefault();
        if (!viewingModule) return;

        try {
            const topicData = {
                ...topicForm,
                moduleId: viewingModule.id,
                order: parseInt(topicForm.order),
                updatedAt: serverTimestamp(),
            };

            if (editingTopic) {
                await updateDoc(doc(db, "readingTopics", editingTopic.id), topicData);
                setTopicUploadStatus({ message: "Topic updated successfully!", type: "success", errors: [] });
            } else {
                await addDoc(collection(db, "readingTopics"), {
                    ...topicData,
                    createdAt: serverTimestamp(),
                });
                setTopicUploadStatus({ message: "Topic added successfully!", type: "success", errors: [] });
            }

            setShowTopicForm(false);
            resetTopicForm();
            await fetchModuleTopics(viewingModule.id);
            setTimeout(() => setTopicUploadStatus({ message: "", type: "", errors: [] }), 3000);
        } catch (error) {
            console.error("Error saving topic:", error);
            setTopicUploadStatus({ message: "Error saving topic", type: "error", errors: [] });
        }
    };

    const handleDeleteTopic = async (topic) => {
        if (!window.confirm("Are you sure you want to delete this topic?")) return;

        try {
            await deleteDoc(doc(db, "readingTopics", topic.id));
            setTopicUploadStatus({ message: "Topic deleted successfully!", type: "success", errors: [] });
            await fetchModuleTopics(viewingModule.id);
            setTimeout(() => setTopicUploadStatus({ message: "", type: "", errors: [] }), 3000);
        } catch (error) {
            console.error("Error deleting topic:", error);
            setTopicUploadStatus({ message: "Error deleting topic", type: "error", errors: [] });
        }
    };

    // Topic CSV Functions
    const downloadTopicCSVTemplate = () => {
        const template = `Title,Content,Order
"Introduction to Topic","<h2>Introduction</h2><p>This is the introduction content.</p><p>You can add images using placeholders like: {{img:your_image.png}}</p>",1
"Advanced Concepts with Image","<h2>Advanced Concepts</h2><p>Here is a diagram:</p>{{img:diagram.png}}<p>The image above shows the concept in detail.</p>",2
"Multiple Images Topic","<h2>Topic with Multiple Images</h2>{{img:image1.jpg}}<p>First image caption.</p>{{img:image2.png}}<p>Second image caption.</p>",3
"Text Only Topic","<h2>Conclusion</h2><p>This topic has no images — placeholders are optional.</p>",4`;
        const blob = new Blob([template], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "topics_template.csv";
        a.click();
    };

    // ==================== ZIP BUNDLE IMPORT FUNCTIONS ====================

    const handleZipFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setZipFile(file);
            setZipImportStatus({ message: "", type: "", details: [] });
        }
    };

    /**
     * Generates a minimal valid 1x1 PNG in base64 for the template ZIP.
     */
    const getPlaceholderPngBase64 = () => {
        // 1x1 transparent PNG bytes in base64
        return "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";
    };

    /**
     * Downloads a sample ZIP bundle with topics.csv + sample images/
     */
    const downloadTopicZipTemplate = async () => {
        try {
            const zip = new JSZip();

            // Sample Markdown files inside ZIP
            const topic1Md = `# Introduction to Hardware Abstraction

Hardware Description Languages (HDLs) like \`SystemVerilog\` and \`VHDL\` allow designers to model digital logic circuits.

:::callout type="info" title="Key Concept"
HDLs describe hardware structure and concurrent behavior rather than sequential software execution.
:::

:::card-grid columns="2"
[card icon="cpu" title="Design Productivity"]
C-like data types and parameterised structures simplify complex digital circuits.
[/card]
[card icon="beaker" title="Verification Power"]
OOP classes and functional coverage enable industrial-strength testbenches.
[/card]
:::

![Diagram](images/intro_diagram.png)
`;

            const topic2Md = `# SystemVerilog FSM Example

Below is a comparison of state machine encoding in Verilog vs SystemVerilog:

:::code-comparison left-title="Legacy Verilog" right-title="SystemVerilog Enum" left-lang="verilog" right-lang="systemverilog"
[left]
parameter IDLE = 2'b00;
reg [1:0] state;
[/left]
[right]
typedef enum logic [1:0] { IDLE, FETCH } state_t;
state_t state;
[/right]
:::
`;

            zip.file("01_introduction.md", topic1Md);
            zip.file("02_fsm_example.md", topic2Md);

            // Add placeholder images (1x1 PNG)
            const pngData = getPlaceholderPngBase64();
            const pngBytes = Uint8Array.from(atob(pngData), c => c.charCodeAt(0));
            zip.folder("images").file("intro_diagram.png", pngBytes);

            // README file
            zip.file("README.txt",
`HOW TO USE THIS MARKDOWN ZIP TEMPLATE
====================================

1. Add your topic Markdown (.md) files to the ZIP (e.g. 01_intro.md, 02_advanced.md).
2. Use relative image links: ![alt text](images/your_image.png).
3. Put matching image files inside the images/ folder.
4. Compress the folder as a .zip and upload it using "Import Bundle (ZIP)".
`);

            const content = await zip.generateAsync({ type: "blob" });
            const url = window.URL.createObjectURL(content);
            const a = document.createElement("a");
            a.href = url;
            a.download = "topics_import_template.zip";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error generating ZIP template:", err);
        }
    };

    /**
     * Replaces all {{img:filename}} tokens in content with proper <img> HTML tags.
     */
    const resolveImagePlaceholders = (content, imageUrlMap) => {
        if (!content) return "";
        let result = content;

        // 1. Resolve {{img:filename.ext}}
        result = result.replace(/\{\{img:([^}]+)\}\}/g, (match, filename) => {
            const cleanName = filename.trim().split("/").pop();
            const url = imageUrlMap[cleanName];
            return url ? `![${cleanName}](${url})` : match;
        });

        // 2. Resolve markdown relative image paths: ![alt](images/filename.ext) or ![alt](filename.ext)
        result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
            const filename = src.split("/").pop();
            const url = imageUrlMap[filename];
            return url ? `![${alt || filename}](${url})` : match;
        });

        return result;
    };

    /**
     * Uploads all images extracted from the ZIP to Firebase Storage.
     * Returns a map of { filename -> downloadUrl }
     */
    const uploadZipImages = async (imageFilesMap, categoryFolder) => {
        const imageUrlMap = {};
        const filenames = Object.keys(imageFilesMap);
        let uploadedCount = 0;

        for (const filename of filenames) {
            try {
                setZipImportProgress({
                    phase: "Uploading Images",
                    current: uploadedCount + 1,
                    total: filenames.length
                });

                const fileData = await imageFilesMap[filename].async("arraybuffer");
                const ext = filename.split(".").pop().toLowerCase();
                const mimeTypes = {
                    png: "image/png",
                    jpg: "image/jpeg",
                    jpeg: "image/jpeg",
                    gif: "image/gif",
                    webp: "image/webp",
                    svg: "image/svg+xml",
                };
                const contentType = mimeTypes[ext] || "image/png";

                const timestamp = Date.now();
                const safeFilename = filename.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9._-]/g, "");
                const storagePath = `textmodule_topics/${categoryFolder}/${timestamp}_${safeFilename}`;
                const storageRef = ref(storage, storagePath);

                const blob = new Blob([fileData], { type: contentType });
                const uploadTask = uploadBytesResumable(storageRef, blob, { contentType });

                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        null,
                        (error) => reject(error),
                        async () => {
                            const downloadUrl = await getDownloadURL(storageRef);
                            imageUrlMap[filename] = downloadUrl;
                            resolve();
                        }
                    );
                });

                uploadedCount++;
                console.log(`Uploaded image: ${filename} → ${imageUrlMap[filename]}`);
            } catch (err) {
                console.error(`Failed to upload image "${filename}":`, err);
                imageUrlMap[filename] = null; // Mark as failed
            }
        }

        return imageUrlMap;
    };

    /**
     * Main orchestrator: extract ZIP → upload images → resolve placeholders → save topics
     */
    const processTopicZipBundle = async () => {
        if (!zipFile || !viewingModule) {
            setZipImportStatus({
                message: "Please select a ZIP bundle file.",
                type: "error",
                details: []
            });
            return;
        }

        if (!auth.currentUser) {
            setZipImportStatus({
                message: "You must be logged in to import topics with images.",
                type: "error",
                details: []
            });
            return;
        }

        setZipImporting(true);
        setZipImportStatus({ message: "", type: "", details: [] });

        try {
            // Phase 1: Extract ZIP
            setZipImportProgress({ phase: "Extracting ZIP", current: 0, total: 0 });
            const zip = await JSZip.loadAsync(zipFile);

            // Look for .md files in the ZIP archive
            const mdFiles = [];
            zip.forEach((relativePath, file) => {
                if (!file.dir && relativePath.toLowerCase().endsWith(".md") && !relativePath.toLowerCase().endsWith("readme.md")) {
                    mdFiles.push({ relativePath, file });
                }
            });

            let topics = [];

            if (mdFiles.length > 0) {
                // Sort .md files alphabetically / by numerical prefix (e.g. 01_intro.md)
                mdFiles.sort((a, b) => a.relativePath.localeCompare(b.relativePath));

                for (let i = 0; i < mdFiles.length; i++) {
                    const text = await mdFiles[i].file.async("text");
                    const filename = mdFiles[i].relativePath.split("/").pop().replace(/\.md$/i, "");
                    const lines = text.trim().split("\n");

                    let title = filename;
                    let content = text.trim();

                    if (lines[0] && lines[0].startsWith("# ")) {
                        title = lines[0].replace(/^#\s*/, "").trim();
                        content = lines.slice(1).join("\n").trim();
                    }

                    const numMatch = filename.match(/^(\d+)/);
                    const order = numMatch ? parseInt(numMatch[1]) : (i + 1);

                    topics.push({
                        title,
                        content,
                        order
                    });
                }
            } else {
                // Fallback: Check for topics.csv
                let csvFile = null;
                zip.forEach((relativePath, file) => {
                    if (!file.dir && relativePath.toLowerCase().endsWith("topics.csv")) {
                        csvFile = file;
                    }
                });

                if (csvFile) {
                    const csvText = await csvFile.async("text");
                    topics = parseTopicCSV(csvText);
                }
            }

            if (topics.length === 0) {
                setZipImporting(false);
                setZipImportStatus({
                    message: "No valid .md files or topics.csv found in the ZIP bundle.",
                    type: "error",
                    details: ["Make sure your ZIP contains topic .md files (e.g., 01_intro.md, 02_advanced.md) or a topics.csv file."]
                });
                return;
            }

            // Phase 2: Find all image files in images/ folder
            const imageFilesMap = {};
            zip.forEach((relativePath, file) => {
                if (!file.dir) {
                    const lowerPath = relativePath.toLowerCase();
                    const isImage = /\.(png|jpg|jpeg|gif|webp|svg)$/.test(lowerPath);
                    // Include files in images/ folder OR any image at root (but not topics.csv)
                    if (isImage) {
                        // Use just the filename as key (for placeholder matching)
                        const filename = relativePath.split("/").pop();
                        imageFilesMap[filename] = file;
                    }
                }
            });

            const imageCount = Object.keys(imageFilesMap).length;
            console.log(`ZIP contains ${topics.length} topics and ${imageCount} images`);

            // Phase 3: Upload images (if any)
            let imageUrlMap = {};
            if (imageCount > 0) {
                const categoryFolder = (viewingModule?.category || "General")
                    .replace(/[^a-zA-Z0-9]/g, "_")
                    .replace(/_+/g, "_")
                    .toLowerCase();

                imageUrlMap = await uploadZipImages(imageFilesMap, categoryFolder);
            }

            // Phase 4: Save topics with resolved image URLs
            setZipImportProgress({ phase: "Saving Topics", current: 0, total: topics.length });
            let successCount = 0;
            const errors = [];

            for (let i = 0; i < topics.length; i++) {
                try {
                    setZipImportProgress({ phase: "Saving Topics", current: i + 1, total: topics.length });

                    // Resolve {{img:filename}} placeholders in content
                    const resolvedContent = resolveImagePlaceholders(
                        topics[i].content || "",
                        imageUrlMap
                    );

                    const topicData = {
                        moduleId: viewingModule.id,
                        title: topics[i].title,
                        content: resolvedContent,
                        order: topics[i].order || (i + 1),
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                    };

                    await addDoc(collection(db, "readingTopics"), topicData);
                    successCount++;
                } catch (err) {
                    console.error(`Error saving topic ${i + 1}:`, err);
                    errors.push(`Topic "${topics[i].title}": ${err.message}`);
                }
            }

            // Phase 5: Done
            const failedImages = Object.entries(imageUrlMap)
                .filter(([, url]) => url === null)
                .map(([name]) => `Image upload failed: ${name}`);

            const allDetails = [...failedImages, ...errors];

            setZipImporting(false);
            setZipImportProgress({ phase: "Done", current: successCount, total: topics.length });

            if (allDetails.length > 0) {
                setZipImportStatus({
                    message: `Imported ${successCount} of ${topics.length} topics with ${allDetails.length} issue(s). Images uploaded: ${imageCount - failedImages.length}/${imageCount}.`,
                    type: "warning",
                    details: allDetails
                });
            } else {
                setZipImportStatus({
                    message: `Successfully imported ${successCount} topic${successCount !== 1 ? "s" : ""} with ${imageCount} image${imageCount !== 1 ? "s" : ""} uploaded! 🎉`,
                    type: "success",
                    details: []
                });
            }

            setShowZipImport(false);
            setZipFile(null);
            await fetchModuleTopics(viewingModule.id);
            setTimeout(() => setZipImportStatus({ message: "", type: "", details: [] }), 8000);

        } catch (err) {
            console.error("ZIP bundle import error:", err);
            setZipImporting(false);
            setZipImportStatus({
                message: `Import failed: ${err.message}`,
                type: "error",
                details: []
            });
        }
    };

    const parseTopicCSV = (text) => {
        const lines = text.split("\n").filter(line => line.trim());
        if (lines.length < 2) return [];

        const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
        const topics = [];

        for (let i = 1; i < lines.length; i++) {
            const values = [];
            let currentValue = "";
            let insideQuotes = false;

            // Parse CSV considering quoted values with commas
            for (let char of lines[i]) {
                if (char === '"') {
                    insideQuotes = !insideQuotes;
                } else if (char === ',' && !insideQuotes) {
                    values.push(currentValue.trim());
                    currentValue = "";
                } else {
                    currentValue += char;
                }
            }
            values.push(currentValue.trim());

            const topic = {};
            headers.forEach((header, index) => {
                const value = values[index] || "";
                if (header === "order") {
                    topic[header] = parseInt(value) || (i);
                } else {
                    topic[header] = value.replace(/^"|"$/g, ''); // Remove surrounding quotes
                }
            });

            if (topic.title && topic.content) {
                topics.push(topic);
            }
        }

        return topics;
    };

    const handleTopicCSVUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setTopicCsvFile(file);
        setTopicUploadStatus({ message: "", type: "", errors: [] });
    };

    const processTopicCSVFile = async () => {
        if (!topicCsvFile || !viewingModule) {
            setTopicUploadStatus({
                message: "Please select a CSV file",
                type: "error",
                errors: []
            });
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target.result;
                const topics = parseTopicCSV(text);

                if (topics.length === 0) {
                    setTopicUploadStatus({
                        message: "No valid topics found in CSV",
                        type: "error",
                        errors: []
                    });
                    return;
                }

                let successCount = 0;
                let errors = [];

                for (let i = 0; i < topics.length; i++) {
                    try {
                        const topicData = {
                            moduleId: viewingModule.id,
                            title: topics[i].title,
                            content: topics[i].content,
                            order: topics[i].order || (i + 1),
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                        };

                        await addDoc(collection(db, "readingTopics"), topicData);
                        successCount++;
                    } catch (err) {
                        console.error(`Error uploading topic ${i + 1}:`, err);
                        errors.push(`Row ${i + 2}: ${err.message}`);
                    }
                }

                if (errors.length > 0) {
                    setTopicUploadStatus({
                        message: `Uploaded ${successCount} topics with ${errors.length} errors`,
                        type: "warning",
                        errors: errors
                    });
                } else {
                    setTopicUploadStatus({
                        message: `Successfully uploaded ${successCount} topics!`,
                        type: "success",
                        errors: []
                    });
                }

                setShowTopicCSVUpload(false);
                setTopicCsvFile(null);
                await fetchModuleTopics(viewingModule.id);
                setTimeout(() => setTopicUploadStatus({ message: "", type: "", errors: [] }), 5000);
            };

            reader.readAsText(topicCsvFile);
        } catch (error) {
            console.error("Error processing CSV:", error);
            setTopicUploadStatus({
                message: "Error processing CSV file",
                type: "error",
                errors: [error.message]
            });
        }
    };

    const handleMarkdownFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length || !viewingModule) return;

        setTopicUploadStatus({ message: "Importing Markdown topics...", type: "info", errors: [] });
        let successCount = 0;
        let errors = [];

        for (let i = 0; i < files.length; i++) {
            try {
                const file = files[i];
                const text = await file.text();
                const filename = file.name.replace(/\.md$/i, "");
                const lines = text.trim().split("\n");

                let title = filename;
                let content = text.trim();

                if (lines[0] && lines[0].startsWith("# ")) {
                    title = lines[0].replace(/^#\s*/, "").trim();
                    content = lines.slice(1).join("\n").trim();
                }

                const numMatch = filename.match(/^(\d+)/);
                const order = numMatch ? parseInt(numMatch[1]) : (moduleTopics.length + i + 1);

                const topicData = {
                    moduleId: viewingModule.id,
                    title,
                    content,
                    order,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                };

                await addDoc(collection(db, "readingTopics"), topicData);
                successCount++;
            } catch (err) {
                console.error(`Error uploading markdown file ${files[i].name}:`, err);
                errors.push(`File ${files[i].name}: ${err.message}`);
            }
        }

        if (errors.length > 0) {
            setTopicUploadStatus({
                message: `Imported ${successCount} Markdown topics with ${errors.length} issue(s)`,
                type: "warning",
                errors
            });
        } else {
            setTopicUploadStatus({
                message: `Successfully imported ${successCount} Markdown topic(s)! 🎉`,
                type: "success",
                errors: []
            });
        }

        await fetchModuleTopics(viewingModule.id);
        setTimeout(() => setTopicUploadStatus({ message: "", type: "", errors: [] }), 5000);
    };

    const stats = {
        total: modules.length,
        published: modules.filter(m => m.isPublished).length,
        draft: modules.filter(m => !m.isPublished).length,
    };

    return (
        <div className={styles.container}>
            {/* Page Header (Catalog View Only) */}
            {activeView === "catalog" && (
                <PageHeader
                    icon={Book}
                    title="Reading & Study Module Management"
                    subtitle="Create, edit, and organize reading modules, markdown study topics, and quizzes for students"
                    badge={`${modules.length} Modules`}
                    actions={
                        <>
                            <button
                                className={styles.secondaryBtn}
                                onClick={() => setShowCSVUpload(true)}
                            >
                                <Upload size={16} /> Import CSV
                            </button>
                            <button
                                className={styles.secondaryBtn}
                                onClick={exportModules}
                            >
                                <Download size={16} /> Export CSV
                            </button>
                            <button
                                className={styles.primaryBtn}
                                onClick={() => handleOpenWorkspace(null, "details")}
                            >
                                <Plus size={16} />Add Module
                            </button>
                        </>
                    }
                />
            )}

            {/* Status Message */}
            {uploadStatus.message && (
                <div className={`alert alert-${uploadStatus.type === "success" ? "success" : uploadStatus.type === "warning" ? "warning" : "danger"} alert-dismissible fade show`}>
                    {uploadStatus.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    {uploadStatus.message}
                    <button type="button" className="btn-close" onClick={() => setUploadStatus({ message: "", type: "" })}></button>
                </div>
            )}

            {/* ==================================================================== */}
            {/* CATALOG VIEW */}
            {/* ==================================================================== */}
            {activeView === "catalog" && (
                <>
                    {/* Sub-Tabs */}
                    <div className={styles.subTabs}>
                        <button
                            className={`${styles.subTab} ${activeSubTab === "modules" ? styles.activeSubTab : ""}`}
                            onClick={() => setActiveSubTab("modules")}
                        >
                            <Book size={16} />
                            Manage Modules
                        </button>
                        <button
                            className={`${styles.subTab} ${activeSubTab === "quizRequests" ? styles.activeSubTab : ""}`}
                            onClick={() => setActiveSubTab("quizRequests")}
                        >
                            <AlertCircle size={16} />
                            Quiz Reset Requests
                        </button>
                    </div>

                    {/* Manage Modules Sub-Tab */}
                    {activeSubTab === "modules" && (
                        <>
                            {/* Stats */}
                            <div className={styles.statsRow}>
                                <div className={styles.statCard}>
                                    <div className={styles.statNumber}>{stats.total}</div>
                                    <div className={styles.statLabel}>Total Modules</div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statNumber}>{stats.published}</div>
                                    <div className={styles.statLabel}>Published</div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statNumber}>{stats.draft}</div>
                                    <div className={styles.statLabel}>Draft</div>
                                </div>
                            </div>

                            {/* Filters */}
                            <div className={styles.card}>
                                <div className={styles.filters}>
                                    <div className={styles.searchBox}>
                                        <Search className={styles.searchIcon} size={16} />
                                        <input
                                            type="text"
                                            className={styles.searchInput}
                                            placeholder="Search modules..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                    <select
                                        className={styles.filterSelect}
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                    >
                                        <option value="all">All Categories</option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                    <select
                                        className={styles.filterSelect}
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="published">Published</option>
                                        <option value="draft">Draft</option>
                                    </select>
                                </div>

                                {/* Modules List */}
                                <div className={styles.testsList}>
                                    {filteredModules.length > 0 ? (
                                        filteredModules.map(module => (
                                            <div key={module.id} className={styles.testCard}>
                                                <div className={styles.testHeader}>
                                                    <div style={{ flex: 1 }}>
                                                        <h5 className={styles.testTitle}>{module.title}</h5>
                                                        <p style={{ fontSize: "0.85rem", color: "#6c757d", marginBottom: "0.5rem" }}>
                                                            {module.description?.substring(0, 100)}...
                                                        </p>
                                                        <div className={styles.badgeRow}>
                                                            <span className={`badge bg-${module.isPublished ? "success" : "secondary"} ${styles.badge}`}>
                                                                {module.isPublished ? "Published" : "Draft"}
                                                            </span>
                                                            <span className={`badge bg-primary ${styles.badge}`}>
                                                                {module.category || "Uncategorized"}
                                                            </span>
                                                            <span className={`badge bg-info ${styles.badge}`}>
                                                                {module.difficulty || "Beginner"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className={styles.testActions}>
                                                        <button
                                                            className={styles.actionBtn}
                                                            onClick={() => handleOpenWorkspace(module, "details")}
                                                            title="Upload & Manage Module Content"
                                                            style={{ fontWeight: 600, color: '#4f46e5' }}
                                                        >
                                                            <Edit size={16} /> Upload & Manage
                                                        </button>
                                                        <button
                                                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                                            onClick={() => handleDelete(module.id)}
                                                            title="Delete Module"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className={styles.testMetaRow}>
                                                    <span>⏱ {module.duration || "N/A"} mins</span>
                                                    <span>📝 {module.tags?.length || 0} tags</span>
                                                    {module.createdAt && (
                                                        <span>📅 {new Date(module.createdAt.seconds * 1000).toLocaleDateString()}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Book size={48} />
                                            <p>No modules found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Quiz Reset Requests Sub-Tab */}
                    {activeSubTab === "quizRequests" && (
                        <div className={styles.card}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
                                <div>
                                    <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: '#1e293b' }}>Quiz Reset Requests</h4>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                        Manage student requests for additional reading module quiz attempts.
                                    </p>
                                </div>
                                <button className={styles.secondaryBtn} onClick={fetchQuizRequests} disabled={loadingRequests}>
                                    {loadingRequests ? "Refreshing..." : "↻ Refresh List"}
                                </button>
                            </div>

                            {loadingRequests ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1rem', color: '#64748b' }}>
                                    <div className={styles.btnSpinner} style={{ width: '24px', height: '24px', border: '3px solid rgba(102, 126, 234, 0.2)', borderTop: '3px solid #667eea', borderRadius: '50%', animation: 'zipSpinner 0.8s linear infinite', marginBottom: '1rem' }}></div>
                                    <p style={{ margin: 0 }}>Fetching reset requests...</p>
                                </div>
                            ) : quizRequests.length > 0 ? (
                                <div className={styles.requestsTableContainer}>
                                    <table className={styles.requestsTable}>
                                        <thead>
                                            <tr>
                                                <th>Student</th>
                                                <th>Reading Module</th>
                                                <th style={{ textAlign: 'center' }}>Requested Limit</th>
                                                <th>Requested On</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {quizRequests.map((req) => (
                                                <tr key={req.id}>
                                                    <td>
                                                        <div style={{ fontWeight: 500, color: '#1e293b' }}>{req.userEmail || "Unknown Email"}</div>
                                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID: {req.userId || "N/A"}</div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: 500, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            {req.moduleTitle || "Untitled Module"}
                                                            {req.moduleId === 'course_quiz' && (
                                                                <span style={{ padding: '1px 6px', borderRadius: '10px', backgroundColor: '#f5f3ff', color: '#7c3aed', fontSize: '0.68rem', fontWeight: 700, border: '1px solid #ddd6fe' }}>Main Exam</span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                            {req.moduleId === 'course_quiz' ? `Course ID: ${req.courseId || "N/A"}` : `Module ID: ${req.moduleId || "N/A"}`}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontWeight: 600, color: '#4f46e5', textAlign: 'center' }}>
                                                        {req.allowedAttempts || 4} attempts
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
                                                            <Calendar size={14} style={{ color: '#64748b' }} />
                                                            <span>
                                                                {req.createdAt
                                                                    ? (req.createdAt.seconds 
                                                                        ? new Date(req.createdAt.seconds * 1000).toLocaleString()
                                                                        : new Date(req.createdAt).toLocaleString())
                                                                    : "N/A"
                                                                }
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`${styles.statusBadge} ${
                                                            req.status === 'approved' 
                                                                ? styles.statusApproved 
                                                                : req.status === 'rejected' 
                                                                    ? styles.statusRejected 
                                                                    : styles.statusPending
                                                        }`}>
                                                            {req.status || "pending"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {req.status === 'pending' ? (
                                                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                                <button 
                                                                    className={`${styles.requestActionBtn} ${styles.approveBtn}`}
                                                                    onClick={() => handleApproveRequest(req.id, req.allowedAttempts)}
                                                                    title="Approve extra attempt"
                                                                >
                                                                    <CheckCircle size={14} /> Approve
                                                                </button>
                                                                <button 
                                                                    className={`${styles.requestActionBtn} ${styles.rejectBtn}`}
                                                                    onClick={() => handleRejectRequest(req.id)}
                                                                    title="Reject request"
                                                                >
                                                                    <XCircle size={14} /> Reject
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                                Processed
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className={styles.emptyState} style={{ padding: '4rem 1rem' }}>
                                    <AlertCircle size={48} style={{ color: '#cbd5e1', marginBottom: '1rem' }} />
                                    <p style={{ margin: 0, fontWeight: 500, color: '#64748b' }}>No reset requests found</p>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                                        Student requests to increase quiz attempts will show up here.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ==================================================================== */}
            {/* IN-PAGE WORKSPACE VIEW */}
            {/* ==================================================================== */}
            {activeView === "workspace" && (
                <div className={styles.workspaceContainer}>
                    {/* Header Banner */}
                    <div className={styles.workspaceHeaderBanner}>
                        <div className={styles.workspaceHeaderTop}>
                            <button
                                type="button"
                                className={styles.backBtn}
                                onClick={() => {
                                    setActiveView("catalog");
                                    setViewingModule(null);
                                    setEditingModule(null);
                                    setModuleTopics([]);
                                    setShowTopicForm(false);
                                }}
                            >
                                <ArrowLeft size={16} /> Back to Modules List
                            </button>
                            {viewingModule && (
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span className={`badge bg-${viewingModule.isPublished ? "success" : "secondary"}`}>
                                        {viewingModule.isPublished ? "Published" : "Draft"}
                                    </span>
                                    <span className="badge bg-primary">
                                        {viewingModule.category || "Uncategorized"}
                                    </span>
                                    <span className="badge bg-info">
                                        {viewingModule.difficulty || "Beginner"}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className={styles.workspaceTitle}>
                                {viewingModule ? viewingModule.title : "Create New Reading Module"}
                            </h3>
                            <p className={styles.workspaceSubtitle}>
                                {viewingModule 
                                    ? `Module ID: ${viewingModule.id} • Topic-wise internal content, live Markdown preview & quiz editor`
                                    : "Configure module metadata, internal markdown topics, and quiz questions below"}
                            </p>
                        </div>
                        {viewingModule && (
                            <div className={styles.workspaceMetaBar}>
                                <div className={styles.workspaceMetaItem}>
                                    <strong>Category:</strong> {viewingModule.category || "N/A"}
                                </div>
                                <div className={styles.workspaceMetaItem}>
                                    <strong>Difficulty:</strong> {viewingModule.difficulty || "Beginner"}
                                </div>
                                <div className={styles.workspaceMetaItem}>
                                    <strong>Duration:</strong> {viewingModule.duration || 0} mins
                                </div>
                                <div className={styles.workspaceMetaItem}>
                                    <strong>Silicon Points:</strong> {viewingModule.siliconPoints ?? 50} pts
                                </div>
                                <div className={styles.workspaceMetaItem}>
                                    <strong>Internal Topics:</strong> {moduleTopics.length} topic(s)
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Workspace In-Page Tabs Navigation Bar */}
                    <div className={styles.workspaceTabsNav}>
                         <button
                                type="button"
                                className={`${styles.workspaceTabBtn} ${workspaceTab === "details" ? styles.activeWorkspaceTabBtn : ""}`}
                                onClick={() => handleWorkspaceTabChange("details")}
                            >
                                <Edit size={16} />
                                Module Details
                            </button>
                        {viewingModule && (
                            <button
                                type="button"
                                className={`${styles.workspaceTabBtn} ${workspaceTab === "topics" ? styles.activeWorkspaceTabBtn : ""}`}
                                onClick={() => handleWorkspaceTabChange("topics")}
                            >
                                <List size={16} />
                                Internal Topics & Markdown ({moduleTopics.length})
                            </button>
                        )}
                       
                        {viewingModule && (
                            <button
                                type="button"
                                className={`${styles.workspaceTabBtn} ${workspaceTab === "quiz" ? styles.activeWorkspaceTabBtn : ""}`}
                                onClick={() => handleWorkspaceTabChange("quiz")}
                            >
                                <HelpCircle size={16} />
                                Module Quiz
                            </button>
                        )}
                    </div>

                    {/* ------------------------------------------------------------------- */}
                    {/* TAB 1: MODULE METADATA & DETAILS */}
                    {/* ------------------------------------------------------------------- */}
                    {workspaceTab === "details" && (
                        <div className={styles.workspaceContentCard}>
                            <h5 style={{ fontWeight: 600, marginBottom: "1.25rem", color: "#1e293b" }}>
                                {viewingModule ? `Edit Module Details — ${viewingModule.title}` : "Create New Module"}
                            </h5>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Module Title *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={moduleForm.title}
                                        onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description *</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={moduleForm.description}
                                        onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                                        required
                                    ></textarea>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Category *</label>
                                        <select
                                            className="form-select"
                                            value={moduleForm.category}
                                            onChange={(e) => setModuleForm({ ...moduleForm, category: e.target.value })}
                                            required
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Difficulty</label>
                                        <select
                                            className="form-select"
                                            value={moduleForm.difficulty}
                                            onChange={(e) => setModuleForm({ ...moduleForm, difficulty: e.target.value })}
                                        >
                                            <option value="Beginner">Beginner</option>
                                            <option value="Intermediate">Intermediate</option>
                                            <option value="Advanced">Advanced</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Duration (minutes)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={moduleForm.duration}
                                            onChange={(e) => setModuleForm({ ...moduleForm, duration: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Author</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={moduleForm.author}
                                            onChange={(e) => setModuleForm({ ...moduleForm, author: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Silicon-Point Value</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="form-control"
                                            placeholder="50"
                                            value={moduleForm.siliconPoints}
                                            onChange={(e) => setModuleForm({ ...moduleForm, siliconPoints: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Tags (comma-separated)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={moduleForm.tags}
                                        onChange={(e) => setModuleForm({ ...moduleForm, tags: e.target.value })}
                                        placeholder="e.g., electronics, circuits, basics"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Overview Content (Optional HTML/Text)</label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        value={moduleForm.content}
                                        onChange={(e) => setModuleForm({ ...moduleForm, content: e.target.value })}
                                        placeholder="Enter optional module summary or overview..."
                                    ></textarea>
                                </div>
                                <div className="form-check mb-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="publishCheckWorkspace"
                                        checked={moduleForm.isPublished}
                                        onChange={(e) => setModuleForm({ ...moduleForm, isPublished: e.target.checked })}
                                    />
                                    <label className="form-check-label" htmlFor="publishCheckWorkspace" style={{ fontWeight: 600 }}>
                                        Publish Module (visible to students)
                                    </label>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setActiveView("catalog");
                                            setViewingModule(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        <Save size={16} /> {viewingModule ? "Save Module Details" : "Create Module"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* ------------------------------------------------------------------- */}
                    {/* TAB 2: INTERNAL TOPICS & MARKDOWN */}
                    {/* ------------------------------------------------------------------- */}
                    {workspaceTab === "topics" && viewingModule && (
                        <div className={styles.workspaceContentCard}>
                            {/* Topic Upload Status Alert */}
                            {topicUploadStatus.message && (
                                <div
                                    className={`alert alert-${topicUploadStatus.type === "success"
                                        ? "success"
                                        : topicUploadStatus.type === "warning"
                                            ? "warning"
                                            : "danger"
                                        } alert-dismissible fade show mb-3`}
                                >
                                    {topicUploadStatus.type === "success" ? (
                                        <CheckCircle size={16} />
                                    ) : (
                                        <AlertCircle size={16} />
                                    )}
                                    {" " + topicUploadStatus.message}
                                    {topicUploadStatus.errors && topicUploadStatus.errors.length > 0 && (
                                        <details style={{ marginTop: "0.5rem" }}>
                                            <summary>View errors ({topicUploadStatus.errors.length})</summary>
                                            <ul style={{ marginBottom: 0, marginTop: "0.5rem" }}>
                                                {topicUploadStatus.errors.map((err, idx) => (
                                                    <li key={idx}>{err}</li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() =>
                                            setTopicUploadStatus({ message: "", type: "", errors: [] })
                                        }
                                    ></button>
                                </div>
                            )}

                            {/* CSV Upload Section */}
                            {showTopicCSVUpload && (
                                <div className={styles.csvUploadCard} style={{ marginBottom: "1rem" }}>
                                    <div className={styles.csvUploadHeader}>
                                        <h6>Import Topics from CSV (Text Only)</h6>
                                        <button
                                            type="button"
                                            className={styles.downloadTemplateBtn}
                                            onClick={downloadTopicCSVTemplate}
                                        >
                                            <Download size={14} />
                                            Download Template
                                        </button>
                                    </div>
                                    <div className={styles.csvInstructions}>
                                        <AlertCircle size={16} />
                                        <p>
                                            Upload a CSV with columns: <strong>Title, Content, Order</strong>.
                                            For topics with images, use <strong>Import Bundle (ZIP)</strong>.
                                        </p>
                                    </div>
                                    <div className={styles.csvUploadArea}>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleTopicCSVUpload}
                                            className={styles.csvFileInput}
                                            id="topicCsvFileInput"
                                        />
                                        <label htmlFor="topicCsvFileInput" className={styles.csvFileLabel}>
                                            {topicCsvFile ? topicCsvFile.name : "Choose CSV file"}
                                        </label>
                                    </div>
                                    <div className={styles.csvUploadActions}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => {
                                                setShowTopicCSVUpload(false);
                                                setTopicCsvFile(null);
                                                setTopicUploadStatus({ message: "", type: "", errors: [] });
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={processTopicCSVFile}
                                            disabled={!topicCsvFile}
                                        >
                                            <Upload size={14} /> Upload Topics
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ZIP Bundle Import Section */}
                            {showZipImport && (
                                <div className={styles.zipImportCard} style={{ marginBottom: "1rem" }}>
                                    <div className={styles.zipImportHeader}>
                                        <div className={styles.zipImportHeaderLeft}>
                                            <Archive size={18} color="#667eea" />
                                            <div>
                                                <h6 style={{ margin: 0 }}>Import Topics + Images (ZIP Bundle)</h6>
                                                <small style={{ color: "#6c757d" }}>Upload multiple topics with images</small>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.downloadTemplateBtn}
                                            onClick={downloadTopicZipTemplate}
                                        >
                                            <Download size={14} /> Download Template ZIP
                                        </button>
                                    </div>

                                    <div className={styles.zipInstructions}>
                                        <div className={styles.zipStep}>
                                            <span className={styles.zipStepNum}>1</span>
                                            <span>Extract template ZIP &amp; add <code>.md</code> topic files</span>
                                        </div>
                                        <div className={styles.zipStep}>
                                            <span className={styles.zipStepNum}>2</span>
                                            <span>Put images in <code>images/</code> folder</span>
                                        </div>
                                    </div>

                                    <div className={styles.zipFilePicker}>
                                        <input
                                            type="file"
                                            accept=".zip"
                                            onChange={handleZipFileChange}
                                            className={styles.csvFileInput}
                                            id="zipBundleFileInput"
                                            disabled={zipImporting}
                                        />
                                        <label htmlFor="zipBundleFileInput" className={`${styles.csvFileLabel} ${styles.zipFileLabel}`}>
                                            <Archive size={16} />
                                            {zipFile ? zipFile.name : "Choose ZIP bundle"}
                                        </label>
                                    </div>

                                    {zipImporting && (
                                        <div className={styles.zipProgressArea}>
                                            <div className={styles.zipProgressPhase}>
                                                <div className={styles.zipProgressSpinner} />
                                                <span>{zipImportProgress.phase} ({zipImportProgress.current}/{zipImportProgress.total})</span>
                                            </div>
                                        </div>
                                    )}

                                    <div className={styles.csvUploadActions}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            disabled={zipImporting}
                                            onClick={() => setShowZipImport(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.zipImportSubmitBtn}
                                            onClick={processTopicZipBundle}
                                            disabled={!zipFile || zipImporting}
                                        >
                                            <Layers size={14} /> Import ZIP Bundle
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ----------------------------------------------------------- */}
                            {/* MASTER-DETAIL SPLIT LAYOUT (25% Sidebar / 75% Editor Pane) */}
                            {/* ----------------------------------------------------------- */}
                            <div className={styles.topicSplitLayout}>
                                {/* LEFT SIDEBAR: 25% TOPIC MASTER LIST */}
                                <div className={styles.topicSidebar}>
                                    <div className={styles.topicSidebarTitleRow}>
                                        <h6>Topics</h6>
                                        <span className={styles.topicCountBadge}>{moduleTopics.length}</span>
                                    </div>

                                    {/* Topic Filter/Search */}
                                    <input
                                        type="text"
                                        className={styles.topicSearchInput}
                                        placeholder="🔍 Search topics..."
                                        value={topicSearchQuery}
                                        onChange={(e) => setTopicSearchQuery(e.target.value)}
                                    />

                                    {/* Action Buttons */}
                                    <div className={styles.topicSidebarActions}>
                                        <button
                                            type="button"
                                            className={`${styles.sidebarActionBtn} ${styles.sidebarPrimaryActionBtn}`}
                                            onClick={handleAddTopic}
                                        >
                                            <Plus size={13} /> Add Topic
                                        </button>
                                        <label className={styles.sidebarActionBtn} style={{ margin: 0, cursor: 'pointer' }}>
                                            <Upload size={12} /> .MD
                                            <input 
                                                type="file" 
                                                accept=".md" 
                                                multiple 
                                                onChange={handleMarkdownFileUpload} 
                                                style={{ display: 'none' }} 
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className={styles.sidebarActionBtn}
                                            onClick={() => {
                                                setShowZipImport(!showZipImport);
                                                setShowTopicCSVUpload(false);
                                            }}
                                        >
                                            <Archive size={12} /> ZIP
                                        </button>
                                    </div>

                                    {/* Scrollable Topics List */}
                                    <div className={styles.topicListScroll}>
                                        {filteredSidebarTopics.length > 0 ? (
                                            filteredSidebarTopics.map((topic) => {
                                                const isSelected = editingTopic?.id === topic.id;
                                                return (
                                                    <div
                                                        key={topic.id}
                                                        className={`${styles.topicListItem} ${isSelected ? styles.topicListItemActive : ""}`}
                                                        onClick={() => handleEditTopic(topic)}
                                                    >
                                                        <div className={styles.topicItemLeft}>
                                                            <span className={styles.topicItemBadge}>#{topic.order}</span>
                                                            <span className={styles.topicItemTitle} title={topic.title}>{topic.title}</span>
                                                        </div>
                                                        <div className={styles.topicItemActions}>
                                                            <button
                                                                type="button"
                                                                className={styles.topicItemBtn}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditTopic(topic);
                                                                }}
                                                                title="Edit Topic"
                                                            >
                                                                <Edit size={13} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`${styles.topicItemBtn} ${styles.topicItemBtnDelete}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteTopic(topic);
                                                                }}
                                                                title="Delete Topic"
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div style={{ textAlign: "center", padding: "2rem 0.5rem", color: "#94a3b8", fontSize: "0.8rem" }}>
                                                No topics found
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* RIGHT MAIN WORKSPACE: 74% EDITOR & PREVIEW PANE */}
                                <div className={styles.topicDetailPane}>
                                    <div className={styles.topicDetailHeader}>
                                        <div className={styles.topicDetailTitle}>
                                            {editingTopic ? (
                                                <>
                                                    <span className="badge bg-primary" style={{ fontSize: "0.72rem", padding: "2px 6px" }}>#{topicForm.order}</span>
                                                    <span style={{ fontSize: "0.9rem" }}>{topicForm.title || "Untitled Topic"}</span>
                                                </>
                                            ) : (
                                                <span style={{ fontSize: "0.9rem" }}>➕ Create New Topic</span>
                                            )}
                                        </div>

                                        <div className={styles.topicPaneModeSwitch}>
                                            <button
                                                type="button"
                                                className={`${styles.paneModeBtn} ${topicRightPaneMode === "edit" ? styles.paneModeBtnActive : ""}`}
                                                onClick={() => setTopicRightPaneMode("edit")}
                                            >
                                                ✏️ Markdown Editor
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.paneModeBtn} ${topicRightPaneMode === "preview" ? styles.paneModeBtnActive : ""}`}
                                                onClick={() => setTopicRightPaneMode("preview")}
                                            >
                                                👁️ Live Preview
                                            </button>
                                        </div>
                                    </div>

                                    {topicRightPaneMode === "edit" ? (
                                        <form onSubmit={handleTopicSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
                                            <div className="row g-2 mb-1">
                                                <div className="col-md-9">
                                                    <label className="form-label mb-0" style={{ fontSize: "0.76rem", fontWeight: 600 }}>Topic Title *</label>
                                                    <input
                                                        type="text"
                                                        name="title"
                                                        className="form-control form-control-sm"
                                                        style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem" }}
                                                        value={topicForm.title}
                                                        onChange={handleTopicInputChange}
                                                        placeholder="e.g. Introduction to UVM Components"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label mb-0" style={{ fontSize: "0.76rem", fontWeight: 600 }}>Order *</label>
                                                    <input
                                                        type="number"
                                                        name="order"
                                                        className="form-control form-control-sm"
                                                        style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem" }}
                                                        value={topicForm.order}
                                                        onChange={handleTopicInputChange}
                                                        min="1"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            {/* Full Width Markdown Editor */}
                                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: 1 }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <label className="form-label mb-0" style={{ fontSize: "0.76rem", fontWeight: 600 }}>Topic Content (Markdown) *</label>
                                                    <small className="text-muted" style={{ fontSize: "0.7rem" }}>Shortcodes enabled</small>
                                                </div>
                                                <textarea
                                                    ref={contentTextareaRef}
                                                    name="content"
                                                    className="form-control form-control-sm"
                                                    style={{ flex: 1, height: "calc(100vh - 310px)", minHeight: "280px", fontFamily: "monospace", fontSize: "0.82rem", padding: "0.5rem", resize: "vertical" }}
                                                    value={topicForm.content}
                                                    onChange={handleTopicInputChange}
                                                    placeholder="Enter Markdown content here..."
                                                    required
                                                ></textarea>
                                            </div>

                                            {/* Topic Image Uploader Section */}
                                            <div style={{ padding: "0.4rem 0.6rem", borderRadius: "6px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                                                <div style={{ fontSize: "0.76rem", fontWeight: 600, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "5px" }}>
                                                    <Book size={13} style={{ color: "#667eea" }} />
                                                    <span>Topic Images (Upload &amp; Click to Insert at Cursor)</span>
                                                </div>
                                                <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageFileChange}
                                                        className="form-control form-control-sm"
                                                        style={{ maxWidth: "220px", fontSize: "0.75rem", padding: "0.2rem 0.4rem" }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={uploadTopicImageToFirebase}
                                                        disabled={!imageFile || uploadingImage}
                                                        style={{ fontSize: "0.72rem", padding: "0.2rem 0.5rem" }}
                                                    >
                                                        {uploadingImage ? "Uploading..." : "Upload Image"}
                                                    </button>
                                                </div>

                                                {uploadingImage && (
                                                    <div className={styles.imageUploadProgressRow} style={{ marginTop: "0.3rem" }}>
                                                        <div className={styles.imageUploadProgressBar}>
                                                            <div 
                                                                className={styles.imageUploadProgressFill} 
                                                                style={{ width: `${imageUploadProgress}%` }}
                                                            />
                                                        </div>
                                                        <span style={{ fontSize: "0.72rem", color: "#555" }}>{imageUploadProgress}%</span>
                                                    </div>
                                                )}

                                                {uploadedImages.length > 0 && (
                                                    <div style={{ marginTop: "0.35rem" }}>
                                                        <small className="text-muted d-block mb-1" style={{ fontSize: "0.7rem" }}>Click image to insert at cursor:</small>
                                                        <div className={styles.uploadedImagesGrid}>
                                                            {uploadedImages.map((url, idx) => (
                                                                <div 
                                                                    key={idx} 
                                                                    className={styles.uploadedImageItem}
                                                                    onClick={() => insertImageAtCursor(url)}
                                                                    title="Click to insert at cursor position"
                                                                >
                                                                    <img src={url} alt={`Uploaded ${idx}`} className={styles.uploadedImageThumbnail} />
                                                                    <div className={styles.uploadedImageInsertOverlay}>
                                                                        <span>Insert</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem", borderTop: "1px solid #f1f5f9", paddingTop: "0.4rem" }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-sm"
                                                    style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}
                                                    onClick={resetTopicForm}
                                                >
                                                    Cancel
                                                </button>
                                                <button type="submit" className="btn btn-primary btn-sm" style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem", display: "flex", alignItems: "center", gap: "3px" }}>
                                                    <Save size={13} />
                                                    {editingTopic ? "Update Topic" : "Save Topic"}
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        /* FULL LIVE PREVIEW MODE - VIEWPORT RESPONSIVE SCROLLABLE CONTAINER */
                                        <div style={{ flex: 1, height: "100%", maxHeight: "100%", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "0.85rem 1rem", background: "#ffffff" }}>
                                            {topicForm.content ? (
                                                <MarkdownContentRenderer content={topicForm.content} />
                                            ) : (
                                                <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#94a3b8" }}>
                                                    <List size={36} />
                                                    <p style={{ marginTop: "0.4rem", fontWeight: 500, fontSize: "0.85rem" }}>No topic content to preview</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ------------------------------------------------------------------- */}
                    {/* TAB 3: MODULE QUIZ EDITOR */}
                    {/* ------------------------------------------------------------------- */}
                    {workspaceTab === "quiz" && viewingModule && (
                        <div className={styles.workspaceContentCard}>
                            <div style={{ background: 'linear-gradient(135deg,#1a0533,#2d1060)', color: '#fff', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h5 style={{ margin: 0, color: '#fff' }}>🧠 Read Module Quiz — {viewingModule.title}</h5>
                                    <small style={{ color: '#c4b5fd' }}>Create & edit quiz questions for this reading module</small>
                                </div>
                                <span style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 12px', borderRadius: '99px', fontWeight: 600, fontSize: '0.85rem' }}>
                                    {quizForm.questions.length} Question(s)
                                </span>
                            </div>

                            {loadingQuiz ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" />
                                    <p className="mt-2 text-muted">Loading quiz data...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Settings Bar */}
                                    <div style={{ background: '#f8f5ff', border: '1px solid #e9e3ff', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.5rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <div>
                                            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6c3ffb', display: 'block', marginBottom: '4px' }}>Passing Score (%)</label>
                                            <input
                                                type="number" min={0} max={100}
                                                value={quizForm.passingScore}
                                                onChange={e => setQuizForm(f => ({ ...f, passingScore: parseInt(e.target.value) || 70 }))}
                                                style={{ width: '90px', padding: '6px', border: '1.5px solid #c4b5fd', borderRadius: '6px', fontWeight: 600 }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6c3ffb', display: 'block', marginBottom: '4px' }}>Questions Per Attempt</label>
                                            <input
                                                type="number" min={5} max={50}
                                                value={quizForm.questionCount}
                                                onChange={e => setQuizForm(f => ({ ...f, questionCount: parseInt(e.target.value) || 20 }))}
                                                style={{ width: '90px', padding: '6px', border: '1.5px solid #c4b5fd', borderRadius: '6px', fontWeight: 600 }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                id="quizPublishWorkspace"
                                                checked={quizForm.isPublished}
                                                onChange={e => setQuizForm(f => ({ ...f, isPublished: e.target.checked }))}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                            />
                                            <label htmlFor="quizPublishWorkspace" style={{ fontWeight: 600, color: '#6c3ffb', cursor: 'pointer', margin: 0 }}>Publish Quiz</label>
                                        </div>
                                    </div>

                                    {/* Questions List */}
                                    {quizForm.questions.map((q, idx) => (
                                        <div key={idx} style={{ border: '1.5px solid #e2d9f3', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem', background: '#fdfcff' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                                                <span style={{ fontWeight: 700, color: '#6c3ffb', fontSize: '0.9rem' }}>Q{idx + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuizForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }))}
                                                    style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '4px 10px', color: '#dc2626', cursor: 'pointer', fontSize: '0.78rem' }}
                                                >✕ Remove</button>
                                            </div>

                                            <textarea
                                                rows={2}
                                                placeholder="Question text *"
                                                value={q.questionText}
                                                onChange={e => {
                                                    const qs = [...quizForm.questions];
                                                    qs[idx] = { ...qs[idx], questionText: e.target.value };
                                                    setQuizForm(f => ({ ...f, questions: qs }));
                                                }}
                                                style={{ width: '100%', marginBottom: '0.6rem', padding: '8px', border: '1px solid #d4c8f0', borderRadius: '6px', resize: 'vertical', fontSize: '0.88rem' }}
                                            />

                                            {['A','B','C','D'].map(opt => (
                                                <div key={opt} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                                                    <span style={{ width: '22px', fontWeight: 700, color: q.correctOption === opt ? '#16a34a' : '#6b7280', fontSize: '0.85rem' }}>{opt}.</span>
                                                    <input
                                                        type="text"
                                                        placeholder={`Option ${opt}`}
                                                        value={q[`option${opt}`] || ''}
                                                        onChange={e => {
                                                            const qs = [...quizForm.questions];
                                                            qs[idx] = { ...qs[idx], [`option${opt}`]: e.target.value };
                                                            setQuizForm(f => ({ ...f, questions: qs }));
                                                        }}
                                                        style={{ flex: 1, padding: '6px', border: `1.5px solid ${q.correctOption === opt ? '#86efac' : '#e5e7eb'}`, borderRadius: '6px', background: q.correctOption === opt ? '#f0fdf4' : '#fff', fontSize: '0.85rem' }}
                                                    />
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.78rem', color: '#16a34a', fontWeight: 600 }}>
                                                        <input
                                                            type="radio"
                                                            name={`correct_${idx}`}
                                                            checked={q.correctOption === opt}
                                                            onChange={() => {
                                                                const qs = [...quizForm.questions];
                                                                qs[idx] = { ...qs[idx], correctOption: opt };
                                                                setQuizForm(f => ({ ...f, questions: qs }));
                                                            }}
                                                        /> Correct
                                                    </label>
                                                </div>
                                            ))}

                                            <input
                                                type="text"
                                                placeholder="Explanation (shown after submit)"
                                                value={q.explanation || ''}
                                                onChange={e => {
                                                    const qs = [...quizForm.questions];
                                                    qs[idx] = { ...qs[idx], explanation: e.target.value };
                                                    setQuizForm(f => ({ ...f, questions: qs }));
                                                }}
                                                style={{ width: '100%', padding: '6px', border: '1px solid #d4c8f0', borderRadius: '6px', fontSize: '0.82rem', color: '#6b7280' }}
                                            />
                                        </div>
                                    ))}

                                    {/* Quiz Action Toolbar */}
                                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                                        <button
                                            type="button"
                                            onClick={() => setQuizForm(f => ({
                                                ...f,
                                                questions: [...f.questions, {
                                                    id: `q_${Date.now()}_${f.questions.length}`,
                                                    questionText: '', optionA: '', optionB: '', optionC: '', optionD: '',
                                                    correctOption: 'A', explanation: '', marks: 1
                                                }]
                                            }))}
                                            style={{ background: 'linear-gradient(135deg,#6c3ffb,#9d5cff)', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
                                        >
                                            + Add Question
                                        </button>

                                        <button
                                            type="button"
                                            onClick={downloadQuizCSVTemplate}
                                            style={{ background: '#f5f3ff', color: '#6c3ffb', border: '1.5px solid #c4b5fd', borderRadius: '8px', padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            📥 Download Quiz CSV Template
                                        </button>

                                        <label style={{ background: '#ede9fe', color: '#6c3ffb', border: '1.5px dashed #9d5cff', borderRadius: '8px', padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem', margin: 0 }}>
                                            📋 Import Quiz CSV
                                            <input
                                                type="file" accept=".csv" style={{ display: 'none' }}
                                                onChange={e => {
                                                    const file = e.target.files[0];
                                                    if (!file) return;
                                                    const reader = new FileReader();
                                                    reader.onload = ev => {
                                                        const lines = ev.target.result.split('\n').filter(l => l.trim());
                                                        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z]/g,''));
                                                        const imported = [];
                                                        for (let i = 1; i < lines.length; i++) {
                                                            const cols = lines[i].split(',');
                                                            const get = key => (cols[headers.indexOf(key)] || '').trim().replace(/^"|"$/g,'');
                                                            imported.push({
                                                                id: `q_csv_${Date.now()}_${i}`,
                                                                questionText: get('questiontext'),
                                                                optionA: get('optiona'),
                                                                optionB: get('optionb'),
                                                                optionC: get('optionc'),
                                                                optionD: get('optiond'),
                                                                correctOption: (get('correctoption') || 'A').toUpperCase(),
                                                                explanation: get('explanation'),
                                                                marks: parseInt(get('marks')) || 1
                                                            });
                                                        }
                                                        setQuizForm(f => ({ ...f, questions: [...f.questions, ...imported] }));
                                                        e.target.value = '';
                                                    };
                                                    reader.readAsText(file);
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {/* Status */}
                                    {quizSaveStatus.message && (
                                        <div className={`alert alert-${quizSaveStatus.type === 'success' ? 'success' : 'danger'} py-2 mb-3`}>
                                            {quizSaveStatus.message}
                                        </div>
                                    )}

                                    {/* Save Quiz */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                        <button
                                            type="button"
                                            onClick={handleSaveQuiz}
                                            disabled={savingQuiz}
                                            style={{ background: 'linear-gradient(135deg,#6c3ffb,#9d5cff)', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 28px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}
                                        >
                                            {savingQuiz ? 'Saving…' : '💾 Save Quiz'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* CSV Upload Modal for Bulk Modules Import in Catalog */}
            {showCSVUpload && (
                <div className={styles.modalOverlay} onClick={() => setShowCSVUpload(false)}>
                    <div className={styles.modal} style={{ maxWidth: "500px" }} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h4>Upload Modules via CSV</h4>
                            <button onClick={() => setShowCSVUpload(false)} className={styles.closeBtn}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>Upload a CSV file with module data. Required columns:</p>
                            <code style={{ fontSize: "0.85rem", display: "block", marginBottom: "1rem" }}>
                                title, description, category, content, duration, difficulty, tags, isPublished, author
                            </code>
                            <button className="btn btn-outline-secondary btn-sm mb-3" onClick={downloadCSVTemplate}>
                                <Download size={14} /> Download Template
                            </button>
                            <input
                                type="file"
                                className="form-control"
                                accept=".csv"
                                onChange={(e) => setCsvFile(e.target.files[0])}
                            />
                        </div>
                        <div className={styles.modalFooter}>
                            <button className="btn btn-secondary" onClick={() => setShowCSVUpload(false)}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleCSVUpload} disabled={!csvFile}>
                                <Upload size={16} /> Upload
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReadModuleManagement;
